import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Gift,
  Loader2,
  Mail,
  Sparkles,
  X,
} from "lucide-react";

const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const firstNameRef = useRef(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    localStorage.setItem("newsletterDismissed", "true"); // Mark as closed
  }, []);

  useEffect(() => {
    const hasSubscribedOrClosed = localStorage.getItem("newsletterDismissed");

    if (!hasSubscribedOrClosed) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000); // Adjust delay as needed
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    firstNameRef.current?.focus();

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, handleClose]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email || !firstName || !lastName) {
      setMessage("Please fill in all fields.");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, firstName, lastName }),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage("Thank you for subscribing! Your free eBook is on its way!");
        setMessageType("success");
        localStorage.setItem("newsletterDismissed", "true"); // Mark as subscribed
        setTimeout(() => setIsOpen(false), 2000); // Close after success
      } else {
        setMessage(result.message || "Something went wrong. Please try again.");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Error submitting subscription:", error);
      setMessage("Server error. Please try again later.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/50 px-4 py-6 backdrop-blur-sm"
          role="presentation"
          onClick={handleClose}
        >
          <div
            className="relative flex max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-md border border-gray-100 bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="newsletter-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="hidden w-5/12 bg-fuchsia-50 p-8 text-gray-900 md:flex md:flex-col md:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-fuchsia-900">
                  <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                  PTE Prep Guide
                </div>
                <h3 className="mt-6 text-3xl font-black leading-tight">
                  Start stronger with a focused study plan.
                </h3>
                <p className="mt-4 text-sm leading-6 text-gray-600">
                  Get practical exam tips, practice reminders, and score-building
                  advice shaped for PTE learners.
                </p>
              </div>

              <div className="mt-8 rounded-md border border-fuchsia-100 bg-white p-5 shadow-sm">
                <img
                  src="/image/191-1919550_education-elements-vector-illustration-education-illustration.png"
                  alt=""
                  className="mx-auto h-52 w-full object-contain"
                />
              </div>
            </div>

            <div className="relative w-full overflow-y-auto p-6 sm:p-8 md:w-7/12">
              <button
                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition hover:bg-fuchsia-50 hover:text-fuchsia-900 focus:outline-none focus:ring-2 focus:ring-fuchsia-800 focus:ring-offset-2"
                onClick={handleClose}
                aria-label="Close"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>

              <div className="pr-10">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-fuchsia-100 text-fuchsia-900">
                  <Gift className="h-5 w-5" aria-hidden="true" />
                </div>
                <p className="mt-5 text-xs font-bold uppercase tracking-wide text-fuchsia-800">
                  Free eBook
                </p>
                <h2
                  id="newsletter-title"
                  className="mt-2 text-2xl font-black leading-tight text-gray-950 sm:text-3xl"
                >
                  Get 10 Secrets to PTE Success
                </h2>
              </div>

              <p className="mt-3 text-sm leading-6 text-gray-600">
                Join the PrepNexis newsletter for expert tips, product updates,
                and a free eBook to sharpen your next study session.
              </p>

              <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-semibold text-gray-800">
                      First name
                    </span>
                    <input
                      ref={firstNameRef}
                      type="text"
                      placeholder="Jane"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                      disabled={isLoading}
                      required
                      className="mt-2 w-full rounded-sm border border-gray-300 px-3 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-fuchsia-800 focus:ring-2 focus:ring-fuchsia-900/10 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-gray-800">
                      Last name
                    </span>
                    <input
                      type="text"
                      placeholder="Smith"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                      disabled={isLoading}
                      required
                      className="mt-2 w-full rounded-sm border border-gray-300 px-3 py-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-fuchsia-800 focus:ring-2 focus:ring-fuchsia-900/10 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-sm font-semibold text-gray-800">
                    Email address
                  </span>
                  <div className="relative mt-2">
                    <Mail
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      aria-hidden="true"
                    />
                    <input
                      type="email"
                      placeholder="jane@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      disabled={isLoading}
                      required
                      className="w-full rounded-sm border border-gray-300 py-3 pl-10 pr-3 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-fuchsia-800 focus:ring-2 focus:ring-fuchsia-900/10 disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </div>
                </label>

                <button
                  type="submit"
                  className="flex w-full items-center justify-center gap-2 rounded-sm bg-gradient-to-r from-fuchsia-900 to-fuchsia-700 px-5 py-3 text-sm font-bold text-white transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-fuchsia-800 focus:ring-offset-2 disabled:cursor-not-allowed disabled:from-gray-500 disabled:to-gray-500"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      Sending...
                    </>
                  ) : (
                    "Get Your eBook"
                  )}
                </button>
              </form>

              {message && (
                <div
                  className={`mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                    messageType === "success"
                      ? "border-green-200 bg-green-50 text-green-800"
                      : "border-red-200 bg-red-50 text-red-700"
                  }`}
                >
                  {messageType === "success" ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  ) : (
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  )}
                  <p>{message}</p>
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-xs text-gray-500">
                <span>No spam. Study-focused updates only.</span>
                <button
                  type="button"
                  onClick={handleClose}
                  className="font-semibold text-fuchsia-800 underline-offset-4 hover:text-fuchsia-950 hover:underline"
                >
                  No thanks
                </button>
              </div>

              <div className="mt-6 flex items-center gap-3 border-t border-gray-100 pt-5 text-xs text-gray-500">
                <img
                  src="/image/prep2.png"
                  alt="PrepNexis"
                  className="h-7 w-auto"
                />
                <span>Trusted preparation from PrepNexis AI.</span>
              </div>
            </div>

            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-fuchsia-900 to-fuchsia-600" />
          </div>
        </div>
      )}
    </>
  );
};

export default NewsletterPopup;
